import { computed, inject, Injectable, Signal } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  PrimeInvoice,
  PrimeInvoiceSaveRequestData,
  PrimeInvoiceSOVData,
} from '@app/framework/constants/prime-invoice.constants';
import { PrimeCommitmentsApiService } from '@app/services/prime-commitments-api.service';
import { NotificationsService } from '@app/services/notifications.service';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { viewProjectActions } from '@app/store/view-project/view-project.actions';
import { VIEW_PROJECT_REFRESH_CAUSE } from '@app/framework/constants/view-project.constants';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { PresignedFileUploadService } from '@app/services/presigned-file-upload.service';

export type PrimeInvoiceState = {
  model: Partial<PrimeInvoice>;
  isLoading: boolean;
  firstPageForm: NgForm | null;
  sovData: PrimeInvoiceSOVData[] | null;
  canBeEdited: boolean;
  originalNumber: string | null;
  hideZeroDollarSOV: boolean;
};

@Injectable({ providedIn: 'root' })
export class PrimeInvoiceStore {
  readonly primeCommitmentsApiService = inject(PrimeCommitmentsApiService);
  readonly notif = inject(NotificationsService);
  readonly store = inject(Store);
  readonly interactionBarStateService = inject(InteractionBarStateService);
  readonly presignedFileUploadService = inject(PresignedFileUploadService);

  readonly state = signalState<PrimeInvoiceState>({
    model: {
      id: null,
      project_id: null,
      files: [],
      status: null,
      invoice_number: '',
      invoice_date: '',
      paid_date: '',
      description: '',
      retainage_version_id: null,
      invoice_items: [],
    },
    sovData: null,
    isLoading: false,
    firstPageForm: null,
    canBeEdited: true,
    originalNumber: null,
    hideZeroDollarSOV: false,
  });
  readonly model = this.state.model;
  readonly firstPageForm = this.state.firstPageForm;
  readonly canBeEdited = this.state.canBeEdited;
  readonly isLoading = this.state.isLoading;
  readonly isEdit = computed(() => {
    const model = this.state.model();
    return model.id && model.id > 0;
  });

  readonly filteredSOVData: Signal<PrimeInvoiceSOVData[]> = computed(() => {
    const invoiceItems: PrimeInvoiceSOVData[] = DeepCopyService.deepCopy(this.state.sovData());
    if (!invoiceItems) {
      return [];
    }

    if (!this.state.hideZeroDollarSOV()) {
      return invoiceItems;
    }

    return invoiceItems.filter(
      (item) =>
        item.current_amount !== 0 ||
        item.retainage_release !== 0 ||
        item.retainage_release_percent !== 0,
    );
  });

  readonly totals: Signal<Partial<PrimeInvoiceSOVData>> = computed(() => {
    const invoiceItems = this.state.sovData();
    if (!invoiceItems) {
      return {
        value: 0,
        previous_invoice_amount: 0,
        current_amount: 0,
        current_retainage: 0,
        invoice_value: 0,
        completed: 0,
        to_complete: 0,
        retainage_balance: 0,
        retainage_release: 0,
      };
    }

    return {
      value: invoiceItems.reduce((acc, item) => acc + item.value, 0),
      previous_invoice_amount: invoiceItems.reduce(
        (acc, item) => acc + item.previous_invoice_amount,
        0,
      ),
      current_amount: invoiceItems.reduce((acc, item) => acc + item.current_amount, 0),
      current_retainage: invoiceItems.reduce((acc, item) => acc + item.current_retainage, 0),
      invoice_value: invoiceItems.reduce((acc, item) => acc + item.invoice_value, 0),
      completed: invoiceItems.reduce((acc, item) => acc + item.completed, 0),
      to_complete: invoiceItems.reduce((acc, item) => acc + item.to_complete, 0),
      retainage_balance: invoiceItems.reduce((acc, item) => acc + item.retainage_balance, 0),
      retainage_release_percent:
        invoiceItems.reduce((acc, item) => acc + item.retainage_release_percent, 0) /
        (invoiceItems?.length ? invoiceItems.length : 1),
      retainage_release: invoiceItems.reduce(
        (acc, item) => acc + (item.retainage_release_percent * item.retainage_balance) / 100,
        0,
      ),
    };
  });

  readonly projectIdTake1$ = this.store.select(viewProjectSelectors.selectProjectId).pipe(
    filter((projectId) => !!projectId),
    take(1),
  );

  clearState() {
    const emptyState: PrimeInvoiceState = {
      model: {
        id: null,
        project_id: null,
        status: null,
        invoice_number: '',
        invoice_date: '',
        paid_date: '',
        description: '',
        retainage_version_id: null,
        invoice_items: [],
      },
      sovData: null,
      canBeEdited: true,
      isLoading: false,
      firstPageForm: null,
      originalNumber: null,
      hideZeroDollarSOV: false,
    };
    patchState(this.state, emptyState);
  }

  updateInvoiceModel(invoice: Partial<PrimeInvoice>) {
    patchState(this.state, { model: { ...this.state.model(), ...invoice } });
  }

  setLoading(isLoading: boolean) {
    patchState(this.state, { isLoading });
  }

  setFirstPageForm(form: NgForm) {
    patchState(this.state, { firstPageForm: form });
  }

  setHideZeroDollarSov(value: boolean) {
    patchState(this.state, { hideZeroDollarSOV: value });
  }

  getNextInvoiceNumber() {
    this.projectIdTake1$
      .pipe(
        switchMap((projectId) => this.primeCommitmentsApiService.getNextInvoiceNumber(projectId)),
      )
      .subscribe({
        next: ({ next_id }) => {
          patchState(this.state, {
            model: { ...this.state.model(), invoice_number: next_id },
            originalNumber: next_id,
          });
        },
        error: (err) => {
          this.notif.showError(
            'Error getting next invoice number: ' +
              (err?.error?.message || 'could not get number.'),
          );
        },
      });
  }

  revertToOriginalInvoiceNumber() {
    const originalNumber = this.state.originalNumber();
    if (originalNumber) {
      patchState(this.state, { model: { ...this.state.model(), invoice_number: originalNumber } });
    } else {
      console.warn('No original invoice number to revert to.');
    }
  }

  isInvoiceNumberValid(newNumber: string) {
    return this.projectIdTake1$.pipe(
      switchMap((projectId) =>
        this.primeCommitmentsApiService.isPINumberUnique(projectId, newNumber),
      ),
      map(() => {
        return true;
      }),
      catchError(() => {
        return of(false);
      }),
    );
  }

  updateInvoiceItem(index: number, updates: { newAmount?: number; newRetainageRate?: number }) {
    const currentInvoiceItems = this.state.sovData();
    const invoiceItem = currentInvoiceItems[index];
    if (!invoiceItem) {
      console.warn('Invalid index for invoice item');
      return;
    }

    const updatedInvoiceSOVData = this.getUpdatedInvoiceSOVData(invoiceItem, updates);
    const updatedSovData = currentInvoiceItems.map((item, i) => {
      if (i === index) {
        return updatedInvoiceSOVData;
      }
      return item;
    });

    patchState(this.state, { sovData: updatedSovData });
  }

  updateAllRetainageRates(newRetainageRate: number) {
    const currentInvoiceItems = this.state.sovData();
    if (!currentInvoiceItems) {
      return;
    }

    const updatedSovData = currentInvoiceItems.map((item) => {
      return this.getUpdatedInvoiceSOVData(item, { newRetainageRate });
    });

    patchState(this.state, { sovData: updatedSovData });
  }

  private getUpdatedInvoiceSOVData(
    invoiceItem: PrimeInvoiceSOVData,
    { newAmount, newRetainageRate }: { newAmount?: number; newRetainageRate?: number },
  ): PrimeInvoiceSOVData {
    if (newRetainageRate == null) {
      newRetainageRate = invoiceItem.retainage_release_percent;
    }
    if (newAmount == null) {
      newAmount = invoiceItem.current_amount;
    }

    const current_retainage = (newAmount * invoiceItem.retainage_rate) / 100;
    return {
      ...invoiceItem,
      current_amount: newAmount,
      current_retainage,
      invoice_value: newAmount - current_retainage,
      completed: invoiceItem.previous_invoice_amount + newAmount,
      to_complete: invoiceItem.value - (invoiceItem.previous_invoice_amount + newAmount),
      retainage_balance: invoiceItem.remaining_retainage + current_retainage,
      retainage_release: (invoiceItem.retainage_balance * newRetainageRate) / 100,
      retainage_release_percent: newRetainageRate,
    };
  }

  loadPrimeInvoice(invoiceId: number) {
    this.setLoading(true);
    this.projectIdTake1$
      .pipe(
        tap((projectId) => {
          patchState(this.state, { model: { ...this.state.model(), project_id: projectId } });
        }),
        switchMap(() => this.primeCommitmentsApiService.getInvoice(invoiceId)),
      )
      .subscribe({
        next: (invoice) => {
          if (!invoice) {
            this.notif.showError('Could not load invoice data.');
          } else {
            const sovData = invoice.invoice_items.map((item) => {
              const retainage_balance =
                (item.remaining_retainage ?? 0) + (item.current_retainage ?? 0);
              return {
                ...item,
                // convert to percentage
                retainage_release_percent:
                  item.remaining_retainage === 0
                    ? 0
                    : (item.retainage_release / retainage_balance) * 100,
                retainage_balance,
                completed: item.previous_invoice_amount + (item.current_amount ?? 0),
                to_complete:
                  item.value - (item.previous_invoice_amount + (item.current_amount ?? 0)),
              };
            });
            patchState(this.state, {
              model: invoice,
              sovData,
              canBeEdited: invoice.is_editable,
              originalNumber: invoice.invoice_number,
            });
          }
          this.setLoading(false);
        },
        error: (err) => {
          this.notif.showError(
            'Error loading invoice: ' + (err?.error?.message || 'could not load invoice.'),
          );
          this.setLoading(false);
        },
      });
  }

  loadSOVData() {
    this.setLoading(true);
    this.projectIdTake1$
      .pipe(
        tap((projectId) => {
          patchState(this.state, { model: { ...this.state.model(), project_id: projectId } });
        }),
        switchMap((projectId) => this.primeCommitmentsApiService.getInvoiceSOVData(projectId)),
        map((data) => {
          return data.map((item) => ({
            ...item,
            current_amount: 0,
            current_retainage: 0,
            retainage_release: 0,
            retainage_release_percent: 0,
            retainage_balance: (item.remaining_retainage ?? 0) + (item.current_retainage ?? 0),
          }));
        }),
      )
      .subscribe({
        next: (data) => {
          patchState(this.state, { sovData: data });
          this.setLoading(false);
        },
        error: (err) => {
          this.notif.showError(
            'Error loading SOV data: ' + err?.message || 'could not load SOV data.',
          );
          this.setLoading(false);
        },
      });
  }

  createInvoice() {
    this.setLoading(true);
    const model: PrimeInvoiceSaveRequestData = this.state.model() as PrimeInvoice;
    model.invoice_items = this.state.sovData().map((item) => {
      return {
        sov_id: item.sov_id,
        current_amount: item.current_amount,
        current_retainage: item.current_retainage,
        retainage_release: item.retainage_balance * (item.retainage_release / 100),
      };
    });

    this.primeCommitmentsApiService
      .createInvoice(model)
      .pipe(
        switchMap((response: PrimeInvoice) => {
          const uploadMetaData = this.model().files?.map((file) => ({
            pi_id: response.id,
          }));
          return this.presignedFileUploadService.uploadMultipleFilesToS3(
            this.model().files ?? [],
            uploadMetaData,
          );
        }),
      )
      .subscribe({
        next: () => {
          this.notif.showSuccess('Invoice created successfully');
          this.store.dispatch(
            viewProjectActions.refreshNeeded({
              cause: VIEW_PROJECT_REFRESH_CAUSE.PRIME_INVOICE_SAVED,
            }),
          );
          this.interactionBarStateService.close();
          this.clearState();
          this.setLoading(false);
        },
        error: (err) => {
          console.error('Error creating invoice:', err);
          this.notif.showError(
            'Error saving invoice: ' + (err?.error?.message || 'could not save invoice.'),
          );
          this.setLoading(false);
        },
      });
  }

  updateInvoice() {
    this.setLoading(true);
    const model: PrimeInvoiceSaveRequestData = { ...this.state.model() } as PrimeInvoice;
    model.invoice_items = this.state.sovData().map((item) => {
      return {
        id: item.id,
        sov_id: item.sov_id,
        current_amount: item.current_amount,
        current_retainage: item.current_retainage,
        retainage_release: item.retainage_release,
      };
    });

    this.primeCommitmentsApiService
      .updateInvoice(model)
      .pipe(
        switchMap((response: PrimeInvoice) => {
          const files = this.model().files ?? [];
          return this.presignedFileUploadService.handleUploadAndDelete(files, {
            pi_id: response.id,
          });
        }),
      )
      .subscribe({
        next: (responses) => {
          if (responses.some((resp) => resp.error)) {
            this.notif.showError('Error occurred during updating attachments.');
          } else {
            this.notif.showSuccess('Invoice updated successfully');
          }

          this.store.dispatch(
            viewProjectActions.refreshNeeded({
              cause: VIEW_PROJECT_REFRESH_CAUSE.PRIME_INVOICE_SAVED,
            }),
          );
          this.interactionBarStateService.close();
          this.clearState();
          this.setLoading(false);
        },
        error: (err) => {
          this.notif.showError(
            'Error updating invoice: ' + (err?.error?.message || 'could not update invoice.'),
          );
          this.setLoading(false);
        },
      });
  }

  deleteInvoice(invoiceId: number) {
    this.setLoading(true);
    this.primeCommitmentsApiService.deleteInvoice(invoiceId).subscribe({
      next: () => {
        this.notif.showSuccess('Invoice deleted successfully');
        this.store.dispatch(
          viewProjectActions.refreshNeeded({
            cause: VIEW_PROJECT_REFRESH_CAUSE.PRIME_INVOICE_SAVED,
          }),
        );
        this.setLoading(false);
      },
      error: (err) => {
        this.notif.showError(
          'Error deleting invoice: ' + (err?.error?.message || 'could not delete invoice.'),
        );
        this.setLoading(false);
      },
    });
  }
}
